import { useState } from "react";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { Text1 } from "@components/ui/texts/Texts";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import { useDataStore } from "@store/user_org";
import { useOrdreStore } from "@store/ordreStore";
import { ordresApi } from "@services/api/ordres";
import { appointmentsApi } from "@services/api/appointments";
import { interventionsApi } from "@services/api/interventions";
import { dossiersApi } from "@services/api/dossiers";
import type { Dossier } from "types/dossier";
import { serviceboxProxy, type AgendaOption } from "@services/serviceboxProxy";
import type { Ordre } from "types/ordre";
import {
  buildCreateRdvParams,
  withVinFromPlate, withAlphaNumber,
  resolveReceptionnaireId,
  resolvePdvId,
  vehicleMarque,
  validateOrdreForTransfer,
  humanizeServiceboxError,
  firstLinkedReceptionnaireId,
  formatDateYYYYMMDD,
  formatTimeHHMM,
} from "@utils/serviceboxTransfer";

/** Transfer state derived from the ordre's ServiceBox fields. */
type TransferState = "full" | "partial" | "none";

function transferState(o: Ordre): TransferState {
  // "full" = an OR exists. Some older ordres carry the OR in `num` (OR-…) without the
  // denormalised `or_number` field — treat those as fully transferred too, so they're
  // never re-transferred (which would duplicate on ServiceBox).
  if (o.or_number || o.num?.startsWith("OR-")) return "full";
  if (o.servicebox_pretransferred_at || o.servicebox_dossier_id) return "partial";
  return "none";
}

/** Per-row live status during a batch run. */
type RowStatus = "idle" | "queued" | "running" | "done" | "error" | "skipped";

interface Row {
  ordre: Ordre;
  status: RowStatus;
  message?: string;
}

const STATE_META: Record<TransferState, { label: string; cls: string }> = {
  full: { label: "Transféré (OR)", cls: "bg-green-100 text-green-700" },
  partial: { label: "Pré-transféré", cls: "bg-orange-100 text-orange-700" },
  none: { label: "Carvis seul", cls: "bg-gray-100 text-gray-600" },
};

const ROW_STATUS_META: Record<RowStatus, { label: string; cls: string } | null> = {
  idle: null,
  queued: { label: "En attente…", cls: "text-gray-400" },
  running: { label: "En cours…", cls: "text-blue-600" },
  done: { label: "Créé sur ServiceBox", cls: "text-green-600" },
  error: { label: "Échec", cls: "text-red-600" },
  skipped: { label: "Ignoré", cls: "text-gray-500" },
};

/** Local YYYY-MM-DD → epoch seconds at the start (00:00) of that day. */
function dayStartSeconds(ymd: string): number {
  const [y, m, d] = ymd.split("-").map(Number);
  return Math.floor(new Date(y, m - 1, d, 0, 0, 0, 0).getTime() / 1000);
}
/** Local YYYY-MM-DD → epoch seconds at the start of the NEXT day (exclusive end). */
function dayEndSeconds(ymd: string): number {
  const [y, m, d] = ymd.split("-").map(Number);
  return Math.floor(new Date(y, m - 1, d + 1, 0, 0, 0, 0).getTime() / 1000);
}

function todayYMD(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Extract a human string from an axios error. FastAPI 422s put an array of validation
 *  objects in `detail`; rendering that array as a React child crashes (React #31). */
function errMsg(e: any, fallback: string): string {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join("; ");
  return e?.message || fallback;
}

function fmtTs(ts?: { seconds: number }): string {
  if (!ts) return "—";
  const d = new Date(ts.seconds * 1000);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * Admin recovery tool: batch-create every Carvis-only RDV onto ServiceBox WITHOUT the
 * Alpha transfer (no OR number). Lets us rebuild ServiceBox from Carvis if it loses data.
 * Reuses the exact single-transfer payload builder so results match the normal flow.
 */
export const ServiceBoxBatchTransfer = () => {
  const orgData = useDataStore((s) => s.orgData);
  const operatorEmail = useDataStore((s) => s.userData?.infos?.email) || "";
  const orgId = orgData?.id;
  const sb = orgData?.servicebox;
  const orgReceptions = orgData?.receptions;

  const [start, setStart] = useState(todayYMD());
  const [end, setEnd] = useState(todayYMD(14));
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [filter, setFilter] = useState<"none" | "partial" | "full" | "all">("none");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // Agenda options (équipes) + forfait catalog, loaded lazily on the first fetch.
  const [equipes, setEquipes] = useState<AgendaOption[]>([]);
  const [receptionnaires, setReceptionnaires] = useState<AgendaOption[]>([]);
  const [forfaitByLabel, setForfaitByLabel] = useState<Record<string, string>>({});

  const creds = {
    username: sb?.username || "",
    password: sb?.password || "",
    site_code: sb?.site_code || "",
    operator_email: operatorEmail,
  };

  const counts = rows
    ? {
        none: rows.filter((r) => transferState(r.ordre) === "none").length,
        partial: rows.filter((r) => transferState(r.ordre) === "partial").length,
        full: rows.filter((r) => transferState(r.ordre) === "full").length,
        all: rows.length,
      }
    : { none: 0, partial: 0, full: 0, all: 0 };

  const fetchRows = async () => {
    if (!orgId) return;
    if (dayEndSeconds(end) <= dayStartSeconds(start)) {
      showNotification({ type: "error", message: "La date de fin doit être après la date de début." });
      return;
    }
    setLoading(true);
    setRows(null);
    setSelected(new Set());
    try {
      // limit at the server max (le=1000): the week/atelier views cap at 200, but a recovery
      // window can hold more, so pass the ceiling to avoid silent truncation. If a window
      // genuinely exceeds 1000, narrow the date range.
      // No sort_by: pairing an order_by with the org_id filter needs a composite Firestore
      // index (org_id + reception_date) that isn't provisioned. The backend already filters
      // the date window in memory, so we just sort client-side below.
      const { items } = await ordresApi.list({
        org_id: orgId,
        date_start: dayStartSeconds(start),
        date_end: dayEndSeconds(end),
        date_field: "reception",
        limit: 1000,
      });
      items.sort((a, b) => (a.reception_date?.seconds || 0) - (b.reception_date?.seconds || 0));
      const newRows: Row[] = items.map((o) => ({ ordre: o, status: "idle" }));
      setRows(newRows);
      // Preselect the Carvis-only rows — the recovery target.
      setSelected(new Set(items.filter((o) => transferState(o) === "none").map((o) => o.id)));

      // Load agenda options + forfait catalog once (best-effort; the run also needs the proxy).
      if (equipes.length === 0) {
        serviceboxProxy
          .getOptions(creds)
          .then((opts) => {
            setEquipes(opts.equipes);
            setReceptionnaires(opts.receptionnaires);
          })
          .catch(() => {
            showNotification({ type: "error", message: "Proxy ServiceBox injoignable — impossible de charger les équipes." });
          });
      }
      if (Object.keys(forfaitByLabel).length === 0) {
        interventionsApi
          .list(orgId)
          .then((list) => {
            const map: Record<string, string> = {};
            list.forEach((it) => {
              if (it.forfait_code) map[it.name.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim()] = it.forfait_code;
            });
            setForfaitByLabel(map);
          })
          .catch(() => {});
      }
    } catch (e: any) {
      showNotification({ type: "error", message: errMsg(e, "Erreur lors de la récupération des ordres") });
    } finally {
      setLoading(false);
    }
  };

  // Guichet carrosserie → équipe CARROSSERIE ; sinon MECANIQUE.
  const defaultEquipeId = (receptionnaireId?: string) => {
    const r = receptionnaires.find((o) => o.id === receptionnaireId);
    const rec = (orgReceptions || []).find((o) => o.servicebox_receptionnaire_id === receptionnaireId);
    const carrosserie = /CARROSS/i.test(`${r?.name || ""} ${rec?.id || ""} ${rec?.label || ""}`)
      && equipes.find((e) => e.name.toUpperCase().includes("CARROSS"));
    const mecanique = equipes.find((e) => e.name.toUpperCase().includes("MECANIQUE"));
    return (carrosserie || mecanique || equipes[0])?.id || "";
  };

  const setRow = (id: string, patch: Partial<Row>) =>
    setRows((prev) => prev?.map((r) => (r.ordre.id === id ? { ...r, ...patch } : r)) ?? null);

  /** Pre-transfer one ordre: create on ServiceBox (no OR) and persist the partial-state fields. */
  const preTransferOne = async (ordre: Ordre): Promise<boolean> => {
    const { canTransfer, missingFields, datesOutOfOrder } = validateOrdreForTransfer(ordre, undefined, {
      requireDossierType: false,
    });
    if (!canTransfer) {
      setRow(ordre.id, {
        status: "skipped",
        message: datesOutOfOrder ? "Dates incohérentes" : `Manquant : ${missingFields.join(", ")}`,
      });
      return false;
    }

    // Column = person: derive the réceptionnaire from the ordre's réception category.
    // Fall back to a real org-configured réception column (RECEPTION 1), NOT the raw
    // first agenda option — that may be a non-bookable personnel → "CCS indisponible".
    const fallbackRecep = firstLinkedReceptionnaireId(orgReceptions, receptionnaires);
    let receptionnaireId = fallbackRecep;
    let restitutionReceptionnaireId = fallbackRecep;
    try {
      const conflict = await appointmentsApi.checkReceptionConflicts({
        ordre_id: ordre.id,
        reception_date: formatDateYYYYMMDD(ordre.reception_date) || undefined,
        reception_time: formatTimeHHMM(ordre.reception_date) || undefined,
        restitution_date: formatDateYYYYMMDD(ordre.restitution) || undefined,
        restitution_time: formatTimeHHMM(ordre.restitution) || undefined,
        org_id: orgId,
      });
      const recLink = resolveReceptionnaireId(conflict.reception?.category, orgReceptions, receptionnaires);
      const restLink = resolveReceptionnaireId(conflict.restitution?.category, orgReceptions, receptionnaires) || recLink;
      if (recLink) receptionnaireId = recLink;
      if (restLink) restitutionReceptionnaireId = restLink;
    } catch {
      /* fall back to first réceptionnaire */
    }

    // Fetch the parent dossier for the authoritative contact: the ordre's denormalised
    // copy often lacks the mobile phone ServiceBox requires, while the dossier's primary
    // contact has it. Mirrors the single-transfer modal. Best-effort — fall back to the
    // ordre's own contacts if the dossier can't be loaded.
    let parentDossier: Dossier | null = null;
    if (ordre.dossier_id) {
      try {
        parentDossier = await dossiersApi.get(ordre.dossier_id);
      } catch {
        /* fall back to the ordre's denormalised contacts */
      }
    }

    const params = await withAlphaNumber(await withVinFromPlate(buildCreateRdvParams({
      ordre,
      parentDossier,
      creds,
      receptionnaireId,
      restitutionReceptionnaireId,
      equipeId: defaultEquipeId(receptionnaireId),
      pdvId: resolvePdvId(sb?.sites, vehicleMarque(ordre.vehicle), sb?.site_code),
      forfaitByLabel,
    }), ordre.org_id), ordre.org_id);

    // ServiceBox rejects a create without a mobile. Skip (don't fail the batch) if truly absent.
    if (!params.tel_mobile?.trim()) {
      setRow(ordre.id, { status: "skipped", message: "Téléphone mobile manquant (client sans mobile)" });
      return false;
    }

    try {
      const result = await serviceboxProxy.createRdvServiceBoxOnly(params, () =>
        setRow(ordre.id, { status: "running", message: "Proxy occupé, attente…" }),
      );
      if (!result.success) {
        const msg = humanizeServiceboxError(result.error || undefined, {
          reception: fmtTs(ordre.reception_date),
          restitution: fmtTs(ordre.restitution),
        });
        setRow(ordre.id, { status: "error", message: msg });
        return false;
      }
      const updatePayload = {
        servicebox_dossier_id: result.dossier_id,
        servicebox_rdv_id: result.rdv_id,
        servicebox_pretransferred_at: new Date().toISOString(),
      };
      const updated = await ordresApi.update(ordre.id, updatePayload as any);
      useOrdreStore.getState().updateOrdre(updated);
      setRow(ordre.id, { status: "done", ordre: updated, message: undefined });
      return true;
    } catch (e: any) {
      setRow(ordre.id, { status: "error", message: e?.message || "Erreur proxy" });
      return false;
    }
  };

  const runBatch = async () => {
    if (!rows) return;
    // Only pre-transfer rows that aren't already on ServiceBox, among the selected.
    const targets = rows.filter((r) => selected.has(r.ordre.id) && transferState(r.ordre) === "none");
    if (targets.length === 0) {
      showNotification({ type: "warning", message: "Aucun ordre à pré-transférer dans la sélection." });
      return;
    }
    setRunning(true);
    setProgress({ done: 0, total: targets.length });
    targets.forEach((t) => setRow(t.ordre.id, { status: "queued", message: undefined }));

    let ok = 0;
    let fail = 0;
    // Sequential: the proxy holds a single ServiceBox session and processes one at a time.
    for (let i = 0; i < targets.length; i++) {
      setRow(targets[i].ordre.id, { status: "running" });
      const success = await preTransferOne(targets[i].ordre);
      if (success) ok++;
      else fail++;
      setProgress({ done: i + 1, total: targets.length });
    }
    setRunning(false);
    showNotification({
      type: fail === 0 ? "success" : "error",
      message: `Pré-transfert terminé : ${ok} créé(s)${fail ? `, ${fail} échec(s)/ignoré(s)` : ""}.`,
    });
  };

  // While a batch runs, keep every row that's part of this run visible even though its
  // state flips to "partial" on success (which would otherwise drop it out of the filter).
  const ACTIVE: RowStatus[] = ["queued", "running", "done", "error", "skipped"];
  const filtered = rows
    ? rows.filter(
        (r) =>
          (filter === "all" ? true : transferState(r.ordre) === filter) ||
          (running && ACTIVE.includes(r.status)),
      )
    : [];

  const selectableIds = filtered.filter((r) => transferState(r.ordre) === "none").map((r) => r.ordre.id);
  const allSelectableSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelectableSelected) selectableIds.forEach((id) => next.delete(id));
      else selectableIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const selectedTargetCount = rows
    ? rows.filter((r) => selected.has(r.ordre.id) && transferState(r.ordre) === "none").length
    : 0;

  if (!sb?.enabled) {
    return (
      <div className="flex flex-col p-8 gap-4 max-w-[1200px]">
        <Text1>Pré-transfert ServiceBox (récupération)</Text1>
        <div className="text-gray-500 text-sm">ServiceBox n'est pas activé pour cette organisation.</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col p-8 gap-6 max-w-[1300px]">
      <div>
        <Text1>Pré-transfert ServiceBox (récupération)</Text1>
        <div className="mt-1 text-gray-500 text-sm max-w-[820px]">
          Crée sur ServiceBox les RDV présents uniquement dans Carvis (client + véhicule + RDV),
          <b> sans générer de numéro OR</b> et sans transfert Alpha. À utiliser pour reconstruire
          ServiceBox à partir de Carvis. Un transfert normal ultérieur ajoutera l'OR.
        </div>
      </div>

      <div className="flex items-end gap-4 flex-wrap">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500">Réception du</label>
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500">au</label>
          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          />
        </div>
        <Button
          label={loading ? "Chargement..." : "Charger les RDV"}
          onClick={fetchRows}
          loading={loading}
          disabled={loading || running}
        />
      </div>

      {rows !== null && (
        <>
          <div className="flex items-center gap-3 justify-between flex-wrap">
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              {([
                ["none", `Carvis seul (${counts.none})`],
                ["partial", `Pré-transférés (${counts.partial})`],
                ["full", `Transférés (${counts.full})`],
                ["all", `Tous (${counts.all})`],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    filter === key ? "bg-white shadow text-blue-600 font-medium" : "text-gray-600 hover:text-gray-800"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              {running && (
                <span className="text-sm text-gray-500">
                  {progress.done}/{progress.total}
                </span>
              )}
              <Button
                label={running ? `Pré-transfert… (${progress.done}/${progress.total})` : `Pré-transférer la sélection (${selectedTargetCount})`}
                onClick={runBatch}
                loading={running}
                disabled={running || loading || selectedTargetCount === 0}
              />
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-gray-500 text-sm py-8 text-center">Aucun ordre pour ce filtre.</div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-3 w-8">
                      <input
                        type="checkbox"
                        checked={allSelectableSelected}
                        onChange={toggleSelectAll}
                        disabled={running || selectableIds.length === 0}
                        title="Tout sélectionner (Carvis seul)"
                      />
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Statut</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">N°</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Client</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Véhicule</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Immat.</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Réception</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Restitution</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Progression</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const st = transferState(r.ordre);
                    const meta = STATE_META[st];
                    const rowStatus = ROW_STATUS_META[r.status];
                    const canSelect = st === "none";
                    return (
                      <tr
                        key={r.ordre.id}
                        className={`border-b last:border-b-0 hover:bg-gray-50 ${
                          r.status === "done" ? "bg-green-50/50" : r.status === "error" ? "bg-red-50/40" : ""
                        }`}
                      >
                        <td className="px-3 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selected.has(r.ordre.id)}
                            onChange={() => toggleOne(r.ordre.id)}
                            disabled={running || !canSelect}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${meta.cls}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-600">{r.ordre.num}</td>
                        <td className="px-4 py-3 font-medium">{r.ordre.name || "—"}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {r.ordre.vehicle?.marque || ""} {r.ordre.vehicle?.modele || r.ordre.modele || ""}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{r.ordre.immatriculation || r.ordre.vehicle?.plate || "—"}</td>
                        <td className="px-4 py-3 text-gray-600">{fmtTs(r.ordre.reception_date)}</td>
                        <td className="px-4 py-3 text-gray-600">{fmtTs(r.ordre.restitution)}</td>
                        <td className="px-4 py-3">
                          {rowStatus && (
                            <span className={`text-xs ${rowStatus.cls}`} title={r.message}>
                              {rowStatus.label}
                              {r.message && r.status !== "done" ? ` — ${r.message}` : ""}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <SecondaryButton
                            label="Voir"
                            variant="small"
                            onClick={() => navigateTo({ route: `/ordre/${r.ordre.id}` })}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};
